import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface ExtensionInfo {
  version: string;
  filename: string;
  latestFilename: string;
  buildDate: string;
  chromeMinVersion: string;
  fileSize: number;
}

type Platform = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  if (ua.includes('chrome')) return 'chrome';
  return 'other';
}

function isDesktop(): boolean {
  return !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    navigator.userAgent.toLowerCase()
  );
}

export function DownloadPage() {
  const [platform, setPlatform] = useState<Platform>('other');
  const [isDesktopDevice, setIsDesktopDevice] = useState(true);
  const [extensionInfo, setExtensionInfo] = useState<ExtensionInfo | null>(null);
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setIsDesktopDevice(isDesktop());

    // Fetch extension info
    fetch('/downloads/extension-info.json')
      .then(res => res.json())
      .then(setExtensionInfo)
      .catch(() => {
        // Fallback info if file doesn't exist
        setExtensionInfo({
          version: '1.1.0',
          filename: 'read-later-extension-latest.zip',
          latestFilename: 'read-later-extension-latest.zip',
          buildDate: new Date().toISOString(),
          chromeMinVersion: '88',
          fileSize: 108000
        });
      });
  }, []);

  const handleDownload = () => {
    setDownloadStarted(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="download-page">
      {/* Navigation */}
      <nav className="download-nav">
        <div className="download-nav-content">
          <Link to="/" className="download-logo">
            <span className="logo-icon">📖</span>
            <span className="logo-text">Read l8r</span>
          </Link>
          <Link to="/app" className="nav-cta">Open App</Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="download-hero">
        <h1>Get the Chrome Extension</h1>
        <p>Save articles with one click from any webpage</p>
      </section>

      {/* Main Content */}
      <section className="download-content">
        {/* Platform Warning */}
        {!isDesktopDevice && (
          <div className="platform-warning">
            <span className="warning-icon">📱</span>
            <div>
              <strong>Mobile device detected</strong>
              <p>Chrome extensions require a desktop browser. Use our web app on mobile!</p>
              <Link to="/app" className="btn-primary">Open Web App</Link>
            </div>
          </div>
        )}

        {platform !== 'chrome' && platform !== 'edge' && isDesktopDevice && (
          <div className="platform-warning">
            <span className="warning-icon">⚠️</span>
            <div>
              <strong>
                {platform === 'firefox' && 'Firefox detected'}
                {platform === 'safari' && 'Safari detected'}
                {platform === 'other' && 'Browser not supported'}
              </strong>
              <p>
                Our extension is built for Chrome and Chromium-based browsers (Chrome, Edge, Brave, Arc).
                {platform === 'firefox' && ' Firefox extension coming soon!'}
              </p>
            </div>
          </div>
        )}

        {/* Download Card */}
        {(platform === 'chrome' || platform === 'edge') && isDesktopDevice && (
          <div className="download-card">
            <div className="download-card-header">
              <div className="extension-icon">🧩</div>
              <div className="extension-info">
                <h2>Read Later Extension</h2>
                {extensionInfo && (
                  <span className="extension-version">
                    v{extensionInfo.version} • {formatFileSize(extensionInfo.fileSize)}
                  </span>
                )}
              </div>
            </div>

            <ul className="extension-features">
              <li>
                <span className="feature-check">✓</span>
                One-click save from any webpage
              </li>
              <li>
                <span className="feature-check">✓</span>
                Automatic article extraction
              </li>
              <li>
                <span className="feature-check">✓</span>
                Sync with your Read l8r account
              </li>
              <li>
                <span className="feature-check">✓</span>
                Works offline
              </li>
            </ul>

            <div className="download-buttons">
              {/* Chrome Web Store - Placeholder */}
              <a
                href="#"
                className="btn-primary btn-chrome-store disabled"
                onClick={(e) => e.preventDefault()}
                title="Coming soon to Chrome Web Store"
              >
                <svg className="chrome-icon" viewBox="0 0 24 24" width="20" height="20">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="4" fill="currentColor"/>
                </svg>
                Chrome Web Store
                <span className="coming-soon-badge">Soon</span>
              </a>

              {/* Direct Download */}
              <a
                href={`/downloads/${extensionInfo?.latestFilename || 'read-later-extension-latest.zip'}`}
                className="btn-secondary"
                download
                onClick={handleDownload}
              >
                <svg className="download-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
                Download ZIP
              </a>
            </div>
          </div>
        )}

        {/* Installation Instructions */}
        {downloadStarted && (
          <div className="install-instructions">
            <h3>Installation Instructions</h3>
            <p className="install-subtitle">Since we're not on the Chrome Web Store yet, follow these steps to install:</p>

            <ol className="install-steps">
              <li>
                <span className="step-number">1</span>
                <div className="step-content">
                  <strong>Unzip the downloaded file</strong>
                  <p>Extract the ZIP file to a folder on your computer. Remember where you put it!</p>
                </div>
              </li>
              <li>
                <span className="step-number">2</span>
                <div className="step-content">
                  <strong>Open Chrome Extensions</strong>
                  <p>
                    Go to <code>chrome://extensions</code> in your address bar, or click Menu → More Tools → Extensions
                  </p>
                </div>
              </li>
              <li>
                <span className="step-number">3</span>
                <div className="step-content">
                  <strong>Enable Developer Mode</strong>
                  <p>Toggle the "Developer mode" switch in the top-right corner</p>
                </div>
              </li>
              <li>
                <span className="step-number">4</span>
                <div className="step-content">
                  <strong>Load the extension</strong>
                  <p>Click "Load unpacked" and select the folder where you extracted the ZIP</p>
                </div>
              </li>
              <li>
                <span className="step-number">5</span>
                <div className="step-content">
                  <strong>Pin the extension</strong>
                  <p>Click the puzzle piece icon in your toolbar and pin Read Later for easy access</p>
                </div>
              </li>
            </ol>

            <div className="install-tip">
              <span className="tip-icon">💡</span>
              <p>
                <strong>Tip:</strong> After installation, click the Read Later icon on any article page to save it.
                Sign in with the same account you use in the web app to sync your reading list!
              </p>
            </div>
          </div>
        )}

        {/* Always show instructions link if download not started */}
        {!downloadStarted && (platform === 'chrome' || platform === 'edge') && isDesktopDevice && (
          <button
            className="show-instructions-btn"
            onClick={() => setDownloadStarted(true)}
          >
            View installation instructions
          </button>
        )}
      </section>

      {/* FAQ Section */}
      <section className="download-faq">
        <h3>Frequently Asked Questions</h3>

        <div className="faq-grid">
          <div className="faq-item">
            <h4>Why isn't it on the Chrome Web Store?</h4>
            <p>We're currently in beta! We'll submit to the Chrome Web Store once we've gathered more feedback. The sideloaded version is identical.</p>
          </div>

          <div className="faq-item">
            <h4>Is it safe to install?</h4>
            <p>Yes! The extension only requests permissions needed for saving articles. It's open source - you can review the code yourself.</p>
          </div>

          <div className="faq-item">
            <h4>Will it auto-update?</h4>
            <p>Sideloaded extensions don't auto-update. Check back here for new versions, or wait for our Chrome Web Store release.</p>
          </div>

          <div className="faq-item">
            <h4>What about Firefox/Safari?</h4>
            <p>We're focused on Chrome first, but Firefox and Safari extensions are on our roadmap!</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="download-footer">
        <p>
          <Link to="/">← Back to Home</Link>
          <span className="separator">•</span>
          <Link to="/app">Open Web App</Link>
        </p>
      </footer>

      <style>{`
        .download-page {
          min-height: 100vh;
          background: var(--bg-primary, #0a0a0a);
          color: var(--text-primary, #ffffff);
        }

        .download-nav {
          position: sticky;
          top: 0;
          background: rgba(10, 10, 10, 0.9);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--border, #2a2a2a);
          z-index: 100;
        }

        .download-nav-content {
          max-width: 1000px;
          margin: 0 auto;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .download-logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
          color: inherit;
        }

        .logo-icon {
          font-size: 1.5rem;
        }

        .logo-text {
          font-weight: 600;
          font-size: 1.125rem;
        }

        .nav-cta {
          padding: 0.5rem 1rem;
          background: var(--accent, #f97316);
          color: white;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 500;
          transition: opacity 0.2s;
        }

        .nav-cta:hover {
          opacity: 0.9;
        }

        .download-hero {
          text-align: center;
          padding: 4rem 2rem 2rem;
        }

        .download-hero h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 0 0 0.5rem;
        }

        .download-hero p {
          color: var(--text-secondary, #888888);
          font-size: 1.125rem;
          margin: 0;
        }

        .download-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 2rem;
        }

        .platform-warning {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: rgba(249, 115, 22, 0.1);
          border: 1px solid rgba(249, 115, 22, 0.3);
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        .warning-icon {
          font-size: 1.5rem;
        }

        .platform-warning strong {
          display: block;
          margin-bottom: 0.25rem;
        }

        .platform-warning p {
          margin: 0 0 0.75rem;
          color: var(--text-secondary, #888888);
          font-size: 0.875rem;
        }

        .download-card {
          background: var(--bg-secondary, #141414);
          border: 1px solid var(--border, #2a2a2a);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .download-card-header {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .extension-icon {
          font-size: 2.5rem;
          background: linear-gradient(135deg, var(--accent, #f97316), #ea580c);
          width: 64px;
          height: 64px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .extension-info h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .extension-version {
          color: var(--text-secondary, #888888);
          font-size: 0.875rem;
        }

        .extension-features {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem;
        }

        .extension-features li {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0;
          color: var(--text-secondary, #888888);
        }

        .feature-check {
          color: #22c55e;
          font-weight: bold;
        }

        .download-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn-primary, .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 8px;
          font-weight: 500;
          text-decoration: none;
          cursor: pointer;
          border: none;
          font-size: 0.9375rem;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--accent, #f97316);
          color: white;
        }

        .btn-primary:hover:not(.disabled) {
          opacity: 0.9;
        }

        .btn-primary.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          position: relative;
        }

        .btn-secondary {
          background: transparent;
          color: var(--text-primary, #ffffff);
          border: 1px solid var(--border, #2a2a2a);
        }

        .btn-secondary:hover {
          background: var(--bg-primary, #0a0a0a);
          border-color: var(--text-secondary, #888888);
        }

        .chrome-icon, .download-icon {
          flex-shrink: 0;
        }

        .coming-soon-badge {
          font-size: 0.625rem;
          padding: 0.125rem 0.375rem;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .install-instructions {
          background: var(--bg-secondary, #141414);
          border: 1px solid var(--border, #2a2a2a);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .install-instructions h3 {
          margin: 0 0 0.5rem;
          font-size: 1.25rem;
        }

        .install-subtitle {
          color: var(--text-secondary, #888888);
          margin: 0 0 1.5rem;
          font-size: 0.875rem;
        }

        .install-steps {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .install-steps li {
          display: flex;
          gap: 1rem;
          padding: 1rem 0;
          border-bottom: 1px solid var(--border, #2a2a2a);
        }

        .install-steps li:last-child {
          border-bottom: none;
        }

        .step-number {
          width: 28px;
          height: 28px;
          background: var(--accent, #f97316);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
          flex-shrink: 0;
        }

        .step-content strong {
          display: block;
          margin-bottom: 0.25rem;
        }

        .step-content p {
          margin: 0;
          color: var(--text-secondary, #888888);
          font-size: 0.875rem;
        }

        .step-content code {
          background: var(--bg-primary, #0a0a0a);
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-size: 0.8125rem;
        }

        .install-tip {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
          padding: 1rem;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.2);
          border-radius: 8px;
        }

        .tip-icon {
          font-size: 1.25rem;
        }

        .install-tip p {
          margin: 0;
          font-size: 0.875rem;
          color: var(--text-secondary, #888888);
        }

        .install-tip strong {
          color: var(--text-primary, #ffffff);
        }

        .show-instructions-btn {
          background: none;
          border: none;
          color: var(--accent, #f97316);
          cursor: pointer;
          font-size: 0.875rem;
          padding: 0;
          text-decoration: underline;
        }

        .show-instructions-btn:hover {
          opacity: 0.8;
        }

        .download-faq {
          max-width: 800px;
          margin: 0 auto;
          padding: 3rem 2rem;
          border-top: 1px solid var(--border, #2a2a2a);
        }

        .download-faq h3 {
          text-align: center;
          margin: 0 0 2rem;
          font-size: 1.5rem;
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .faq-item {
          background: var(--bg-secondary, #141414);
          border-radius: 12px;
          padding: 1.25rem;
        }

        .faq-item h4 {
          margin: 0 0 0.5rem;
          font-size: 0.9375rem;
        }

        .faq-item p {
          margin: 0;
          color: var(--text-secondary, #888888);
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .download-footer {
          text-align: center;
          padding: 2rem;
          border-top: 1px solid var(--border, #2a2a2a);
          color: var(--text-secondary, #888888);
        }

        .download-footer a {
          color: var(--accent, #f97316);
          text-decoration: none;
        }

        .download-footer a:hover {
          text-decoration: underline;
        }

        .separator {
          margin: 0 1rem;
        }

        @media (max-width: 600px) {
          .download-hero h1 {
            font-size: 1.75rem;
          }

          .download-content {
            padding: 1rem;
          }

          .download-buttons {
            flex-direction: column;
          }

          .btn-primary, .btn-secondary {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}
